import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import usePayroll from "../../hooks/usePayroll";
import usePayTransaction from "../../hooks/usePayTransaction";
import useExpense from "../../hooks/useExpense";
import useCall from "../../hooks/useCall";
import useAdjustment from "../../hooks/useAdjustment";
import useProject from "../../hooks/useProject";
import useExpenseAccount from "../../hooks/useExpenseAccount";
import useData from "../../hooks/useData";

import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";

import Page from "./Page";
import Card from "../layout/Card";
import PayrollExports from "../ui/PayrollExports";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import CallDocketModal from "../modals/CallDocketModal";
import AdjustmentModal from "../modals/AdjustmentModal";
import ExpenseApprovalModal from "../modals/ExpenseApprovalModal";

const PayPeriodProcessing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId: currentUserId } = useSelector(
    (state) => state.authentication,
  );

  // Hooks
  const { getPayPeriod, refreshPayPeriod, updatePayPeriod, togglePayrollLock } =
    usePayroll();
  const {
    transactions,
    loading: transactionsLoading,
    getByPayPeriod,
    getSummaryByUser,
    getChangedCalls,
    reprocessPayPeriod,
    closePayPeriod,
    applyPayroll,
    removeCallFromPayPeriod,
  } = usePayTransaction();

  const {
    pendingExpenses,
    expensesLoading,
    getPendingApprovals,
    approveExpense,
    rejectExpense,
    getById: getExpenseById,
  } = useExpense();
  const { projects = [], getProjects } = useProject();
  const { expenseAccounts = [], loadExpenseAccounts } = useExpenseAccount();
  const { getFromServer } = useData();

  const { getCallById, getCallFullListById } = useCall();

  const {
    adjustments,
    getByPayPeriodId: getAdjustments,
    add: addAdjustment,
    update: updateAdjustment,
    deleteAdjustment,
    approve: approveAdjustment,
  } = useAdjustment();

  const backDestination = location.state?.backDestination;
  const payPeriodId = location.state?.payPeriodId;
  const payPeriod = getPayPeriod(payPeriodId);

  const [activeTab, setActiveTab] = useState(0);
  const [userSummary, setUserSummary] = useState([]);
  const [processedData, setProcessedData] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    expense: null,
    initialAction: "approve",
  });
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [payrollLocked, setPayrollLocked] = useState(
    payPeriod?.payrollLocked || false,
  );

  // Phase 4 state
  const [changedCallsInfo, setChangedCallsInfo] = useState(null);
  const [showChangedCallsDialog, setShowChangedCallsDialog] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [fullReprocess, setFullReprocess] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [selectedCallForEdit, setSelectedCallForEdit] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [lastProcessingStatus, setLastProcessingStatus] = useState(null);
  const [receiptDialog, setReceiptDialog] = useState({
    open: false,
    receipts: [],
    expenseDescription: "",
  });

  // Adjustments state
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [adjustmentUsers, setAdjustmentUsers] = useState([]);
  const [adjustmentTypes, setAdjustmentTypes] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    adjustmentId: null,
  });

  const adjustmentProjects = (Array.isArray(projects) ? projects : [])
    .filter((project) => project?.isActive === true)
    .map((project) => ({
      id: project.id,
      key: project.id,
      name: project.name || "Unnamed Project",
    }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const loadData = useCallback(async () => {
    if (!payPeriodId) return;
    await getByPayPeriod(payPeriodId);
    const users = (await getSummaryByUser(payPeriodId)) || [];

    // Fetch adjustments to add to totals
    const adjData = await getAdjustments(payPeriodId);
    const periodAdjustments = (adjData || []).filter(
      (adjustment) => adjustment.payPeriodId === payPeriodId,
    );

    // Calculate adjustment amounts by user and add to totals
    const usersWithAdjustments = users.map((user) => {
      const userAdjustments = periodAdjustments.filter(
        (a) => a.user?.id === user.userId,
      );
      const adjustmentAmount = userAdjustments.reduce(
        (sum, a) => sum + (a.amount || 0),
        0,
      );
      return {
        ...user,
        totalAmount: (user.totalAmount || 0) + adjustmentAmount,
      };
    });

    const userSummaryIds = new Set(
      usersWithAdjustments.map((user) => String(user.userId || "")),
    );

    const adjustmentOnlyUsers = [];
    for (const adjustment of periodAdjustments) {
      const adjustmentUserId = String(adjustment?.user?.id || "");
      if (!adjustmentUserId || userSummaryIds.has(adjustmentUserId)) continue;

      userSummaryIds.add(adjustmentUserId);
      adjustmentOnlyUsers.push({
        userId: adjustmentUserId,
        userName: adjustment?.user?.name || "Unknown",
        totalAmount: Number(adjustment.amount || 0),
        transactionCount: 0,
        callCount: 0,
        mileageCount: 0,
        adjustmentCount: 1,
      });
    }

    const combinedSummary = [...usersWithAdjustments, ...adjustmentOnlyUsers];

    setUserSummary(combinedSummary || []);
    await getPendingApprovals();

    // Build adjustment users list from all payroll-eligible users
    const payrollUsers =
      (await getFromServer({
        entityName: "User",
        filter: { includeInPayroll: true },
      })) || [];

    const userList = (Array.isArray(payrollUsers) ? payrollUsers : [])
      .filter((user) => user.includeInPayroll === true)
      .map((user) => ({
        id: user.id,
        key: user.id,
        name: user.legalName || user.name || "Unknown",
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));

    setAdjustmentUsers(userList);

    const types = (await getFromServer({ entityName: "AdjustmentType" })) || [];
    const normalizedTypes = (Array.isArray(types) ? types : [])
      .filter((type) => type.isActive !== false)
      .sort((a, b) => {
        const sortA = Number.isFinite(a?.sortOrder) ? a.sortOrder : 0;
        const sortB = Number.isFinite(b?.sortOrder) ? b.sortOrder : 0;
        if (sortA !== sortB) return sortA - sortB;
        return String(a?.label || a?.key || "").localeCompare(
          String(b?.label || b?.key || ""),
        );
      });
    setAdjustmentTypes(normalizedTypes);
  }, [payPeriodId]);

  useEffect(() => {
    if (payPeriodId) {
      loadData();
    }
  }, [payPeriodId]);

  useEffect(() => {
    getProjects({ isActive: true });
    loadExpenseAccounts();
  }, []);

  useEffect(() => {}, [transactions, userSummary]);

  // Sync payrollLocked state when payPeriod updates
  useEffect(() => {
    if (payPeriod?.payrollLocked !== undefined) {
      setPayrollLocked(payPeriod.payrollLocked);
    }
  }, [payPeriod?.payrollLocked]);

  // Poll for pay period status updates
  useEffect(() => {
    if (!payPeriod || !payPeriodId) return;

    const shouldPoll =
      payPeriod.processingStatus === "queued" ||
      payPeriod.processingStatus === "inProgress";

    if (!shouldPoll) return;

    const pollInterval = setInterval(async () => {
      const updated = await refreshPayPeriod(payPeriodId);
      if (!updated) return;

      // When the worker finishes, reload the screen data and notify
      if (updated.processingStatus === "completed") {
        await loadData();
        setMessageDialog({
          open: true,
          title: "Payroll Processing Complete",
          message:
            "Payroll processing has finished and the data has been refreshed.",
        });
        setLastProcessingStatus("completed");
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [payPeriod, payPeriodId, refreshPayPeriod, loadData]);

  // Detect when processing completes and show completion dialog
  useEffect(() => {
    if (
      payPeriod &&
      (lastProcessingStatus === "queued" ||
        lastProcessingStatus === "inProgress") &&
      payPeriod.processingStatus === "completed"
    ) {
      (async () => {
        await loadData();
        setMessageDialog({
          open: true,
          title: "Payroll Processing Complete",
          message:
            "Payroll processing has finished and the data has been refreshed.",
        });
      })();
    }
    setLastProcessingStatus(payPeriod?.processingStatus);
  }, [payPeriod?.processingStatus, lastProcessingStatus, loadData]);

  const handleProcessPayroll = async () => {
    const result = await applyPayroll(currentUserId || "system");
    if (result) {
      setProcessedData(result);
      // Refresh pay period so polling starts immediately and UI reflects status
      await refreshPayPeriod(payPeriodId);
      await loadData();
      setLastProcessingStatus("queued");
    }
  };

  const shouldUseReprocessFlow = () => {
    const hasTransactions =
      Array.isArray(transactions) && transactions.length > 0;
    const wasProcessed =
      payPeriod?.processingStatus === "completed" ||
      payPeriod?.processingStatus === "failed" ||
      !!payPeriod?.processedOn;

    return hasTransactions || wasProcessed;
  };

  const handleProcessOrReprocessClick = () => {
    setProcessedData(null);

    if (shouldUseReprocessFlow()) {
      handleReprocessClick();
      return;
    }

    handleProcessPayroll();
  };

  const handleTogglePayrollLock = async (locked) => {
    if (!payPeriod) return;
    try {
      // Update local state immediately for instant UI feedback
      setPayrollLocked(locked);

      // Call backend action that cascades to all transactions
      const result = await togglePayrollLock(payPeriodId, locked);

      if (result) {
        const action = locked ? "locked" : "unlocked";
        setMessageDialog({
          open: true,
          title: "Success",
          message: `Pay period has been ${action} for payroll processing.`,
        });
      } else {
        // Revert local state if call failed
        setPayrollLocked(!locked);
      }
    } catch (err) {
      console.error("[handleTogglePayrollLock] Error:", err);
      // Revert local state on error
      setPayrollLocked(!locked);
      setMessageDialog({
        open: true,
        title: "Error",
        message: "Failed to update payroll lock status.",
      });
    }
  };

  const openApprovalDialog = async (expense, initialAction) => {
    const expenseId = expense?.id || expense?._id;
    const fullExpense = (await getExpenseById(expenseId)) || expense;
    setApprovalDialog({
      open: true,
      expense: fullExpense,
      initialAction,
    });
  };

  const closeApprovalDialog = () => {
    setApprovalDialog({ open: false, expense: null, initialAction: "approve" });
  };

  const handleApproveExpense = async (editedFields) => {
    if (!approvalDialog.expense) return;

    setApprovalSubmitting(true);
    try {
      await approveExpense(
        approvalDialog.expense.id || approvalDialog.expense._id,
        currentUserId || "system",
        editedFields?.notes || "",
        editedFields,
      );
      closeApprovalDialog();
      await getPendingApprovals();
      await loadData();
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleRejectExpense = async (rejectionReason, editedFields) => {
    if (!approvalDialog.expense) return;

    setApprovalSubmitting(true);
    try {
      await rejectExpense(
        approvalDialog.expense.id || approvalDialog.expense._id,
        currentUserId || "system",
        rejectionReason,
        editedFields,
      );
      closeApprovalDialog();
      await getPendingApprovals();
      await loadData();
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleToolbarClick = (action) => {
    switch (action) {
      case "back":
        if (backDestination) {
          navigate(backDestination);
        }
        break;
      default:
        break;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (value) => {
    return `$${value?.toFixed(2) || "0.00"}`;
  };

  const getExpenseTypeLabel = (expense) => {
    return (
      expense?.expenseAccount?.name ||
      expense?.expenseCategory ||
      expense?.expenseType ||
      "-"
    );
  };

  const getExpenseValueLabel = (expense) => {
    if (expense?.expenseCategory === "hours") {
      return `${expense?.minutes || 0} min`;
    }
    if (expense?.expenseCategory === "mileage") {
      return `${expense?.mileage || 0} km`;
    }
    return formatCurrency(expense?.amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "";
    return format(new Date(date), "MMM dd, yyyy");
  };

  // Helper to enrich transaction with call details
  const enrichTransactionWithCallDetails = (transaction) => {
    if (
      transaction.transactionType === "call" ||
      transaction.transactionType === "mileage" ||
      transaction.transactionType === "homeMileage"
    ) {
      // const call = transaction.call || {};
      return {
        ...transaction,
        projectName: transaction.project?.name || "Unknown",
        locationName: transaction.location?.name || "Unknown",
        locationCity: transaction.location?.address?.city || "",
        locationProvince: transaction.location?.address?.province || "",
        locationNumber: transaction.location?.locationNumber || "",
        scheduleDate: transaction.transactionDate || new Date(),
        hoursWorked: transaction.calculationDetails?.hoursWorked || 0,
        hourlyRate: transaction.calculationDetails.hourlyRate || 0,
        duration: transaction.calculationDetails.hoursWorked * 60 || 0,
        mileage:
          (transaction.calculationDetails?.callMileage || 0) +
            (transaction.calculationDetails?.homeMileage || 0) ||
          transaction.calculationDetails?.mileage ||
          transaction.calculationDetails?.distance ||
          0,
      };
    }
    return transaction;
  };

  // Phase 4 Handler Functions
  const handleViewChangedCalls = async () => {
    setIsProcessing(true);
    const result = await getChangedCalls(payPeriodId);
    const hasChanges =
      result?.changedCalls?.length > 0 || result?.newExpenses?.length > 0;

    if (result && hasChanges) {
      setChangedCallsInfo(result);
      setShowChangedCallsDialog(true);
    } else {
      setMessageDialog({
        open: true,
        title: "No Changes Detected",
        message: "No changes have been detected for this pay period.",
      });
    }
    setIsProcessing(false);
  };

  const handleReprocessClick = () => {
    setFullReprocess(false);
    setShowReprocessDialog(true);
  };

  const handleConfirmReprocess = async () => {
    setIsProcessing(true);
    const result = await reprocessPayPeriod(
      payPeriodId,
      fullReprocess,
      currentUserId || "system",
    );
    if (result) {
      setShowReprocessDialog(false);
      setFullReprocess(false);
      // Refresh to get the updated processingStatus = "queued"
      await refreshPayPeriod(payPeriodId);
      // Don't show completion dialog yet - wait for polling to detect completion
      // The polling effect will reload data when status changes to "completed"
      setMessageDialog({
        open: true,
        title: "Reprocessing Queued",
        message:
          "Pay period reprocessing has been queued. You will receive an email when complete.",
      });
    }
    setIsProcessing(false);
  };

  const handleCloseClick = () => {
    setShowCloseDialog(true);
  };

  const handleConfirmClose = async () => {
    setIsProcessing(true);
    const result = await closePayPeriod(payPeriodId);
    if (result) {
      setShowCloseDialog(false);
      await loadData();
      setMessageDialog({
        open: true,
        title: "Pay Period Closed",
        message:
          "Pay period has been successfully closed. All calls and expenses are now locked and cannot be modified.",
      });
    }
    setIsProcessing(false);
  };

  const handleEditCall = async (callId) => {
    const call = await getCallById(callId);

    setSelectedCallForEdit(call);
  };

  const handleRemoveCall = async (callId) => {
    if (!callId || !payPeriodId) return;
    const result = await removeCallFromPayPeriod(
      callId,
      payPeriodId,
      currentUserId,
    );
    if (result) {
      await loadData();
    }
  };

  const handleViewReceipts = async (expenseId, description) => {
    try {
      const expense = await getExpenseById(expenseId);
      if (expense && expense.receipts && expense.receipts.length > 0) {
        setReceiptDialog({
          open: true,
          receipts: expense.receipts,
          expenseDescription: description || expense.description || "Expense",
        });
      }
    } catch (err) {
      console.error("Error loading receipts:", err);
    }
  };

  const handleCloseReceiptDialog = () => {
    setReceiptDialog({ open: false, receipts: [], expenseDescription: "" });
  };

  // Adjustment Handlers
  const handleSaveAdjustment = async (adjustmentData) => {
    try {
      let result;
      if (selectedAdjustment) {
        result = await updateAdjustment(adjustmentData);
      } else {
        result = await addAdjustment(adjustmentData);
      }

      if (result) {
        setShowAdjustmentModal(false);
        setSelectedAdjustment(null);
        await loadData();
      }
    } catch (err) {
      console.error("[handleSaveAdjustment] Error:", err);
    }
  };

  const handleDeleteAdjustment = async (adjustmentId) => {
    try {
      const result = await deleteAdjustment(adjustmentId);
      if (result) {
        await loadData();
      }
    } catch (err) {
      console.error("[handleDeleteAdjustment] Error:", err);
    }
  };

  const handleApproveAdjustmentClick = async (adjustmentId) => {
    try {
      const result = await approveAdjustment(adjustmentId);
      if (result) {
        await loadData();
      }
    } catch (err) {
      console.error("[handleApproveAdjustmentClick] Error:", err);
    }
  };

  return (
    <>
      <Page
        pageName="payPeriod_processing"
        entityName={payPeriod?.name}
        includeToolbar={true}
        items={[
          {
            name: "back",
            iconName: "ArrowCircleLeft",
            hide: backDestination ? false : true,
          },
        ]}
        onToolItemClick={handleToolbarClick}
      >
        <Card>
          <Stack spacing={2}>
            {!payPeriod ? (
              <Typography
                variant="body1"
                color="error"
              >
                Loading pay period... (ID: {payPeriodId})
              </Typography>
            ) : (
              <>
                {/* Header: Pay Period Info and Status Badge on One Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ m: 0 }}
                    >
                      {payPeriod?.name}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(payPeriod?.startDate)} -{" "}
                      {formatDate(payPeriod?.endDate)}
                    </Typography>
                    <Typography variant="body2">
                      Process Date: {formatDate(payPeriod?.processDate)}
                    </Typography>
                    <Typography variant="body2">
                      Status: {payPeriod?.status || "future"}
                    </Typography>
                  </Box>

                  {/* Processing Status Badge */}
                  {payPeriod?.processingStatus && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {(() => {
                        const statusConfig = {
                          unprocessed: {
                            label: "Unprocessed",
                            color: "default",
                          },
                          queued: { label: "Queued", color: "info" },
                          inProgress: {
                            label: "Processing...",
                            color: "warning",
                          },
                          completed: {
                            label: "Completed",
                            color: "success",
                          },
                          failed: { label: "Failed", color: "error" },
                        };
                        const config =
                          statusConfig[payPeriod.processingStatus] ||
                          statusConfig.unprocessed;
                        return (
                          <Box
                            component="span"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor:
                                config.color === "success"
                                  ? "#c8e6c9"
                                  : config.color === "warning"
                                    ? "#fff9c4"
                                    : config.color === "error"
                                      ? "#ffcdd2"
                                      : config.color === "info"
                                        ? "#bbdefb"
                                        : "#e0e0e0",
                              color:
                                config.color === "success"
                                  ? "#2e7d32"
                                  : config.color === "warning"
                                    ? "#f57f17"
                                    : config.color === "error"
                                      ? "#c62828"
                                      : config.color === "info"
                                        ? "#1565c0"
                                        : "#424242",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {config.label}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}
                </Box>

                {/* Buttons Row */}
                <Box sx={{ mb: 2 }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ flexWrap: "wrap" }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleProcessOrReprocessClick}
                      disabled={
                        isProcessing ||
                        transactionsLoading ||
                        payPeriod?.processingStatus === "queued" ||
                        payPeriod?.processingStatus === "inProgress"
                      }
                    >
                      {shouldUseReprocessFlow()
                        ? "Reprocess Payroll"
                        : "Process Payroll"}
                    </Button>
                    {processedData && (
                      <Typography
                        variant="body2"
                        sx={{ pt: 1 }}
                      >
                        Processed: {processedData.summary.totalTransactions}{" "}
                        transactions
                        {processedData.summary.errorTransactions > 0 &&
                          ` (${processedData.summary.errorTransactions} errors)`}
                      </Typography>
                    )}
                    {payPeriod?.status === "open" && (
                      <>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleCloseClick}
                          disabled={
                            isProcessing ||
                            payPeriod?.processingStatus === "queued" ||
                            payPeriod?.processingStatus === "inProgress"
                          }
                        >
                          Close Pay Period
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>

                {/* Payroll Lock Status Card */}
                <Card sx={{ mt: 2, mb: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2 }}
                    >
                      Payroll Processing Status
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={payrollLocked}
                          onChange={(e) =>
                            handleTogglePayrollLock(e.target.checked)
                          }
                          disabled={
                            !payPeriod ||
                            payPeriod?.processingStatus === "queued" ||
                            payPeriod?.processingStatus === "inProgress"
                          }
                        />
                      }
                      label="Payroll Processing Locked"
                    />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      {payrollLocked
                        ? "Locked: Calls, expenses, and adjustments cannot be edited."
                        : "Unlocked: Changes to calls, expenses, and adjustments are allowed."}
                    </Typography>
                  </Box>
                </Card>

                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                  >
                    <Tab label="Summary" />
                    <Tab label="Transactions" />
                    <Tab label="Pending Expenses" />
                    <Tab label="Adjustments" />
                    <Tab label="Exports" />
                  </Tabs>
                </Box>

                {/* Summary Tab */}
                {activeTab === 0 && (
                  <Box>
                    {/* Summary Table */}
                    <Box sx={{ mt: 2, overflow: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: "2px solid #ccc" }}>
                            <th style={{ padding: "8px", width: "30px" }}>
                              {/* Expand button column */}
                            </th>
                            <th style={{ padding: "8px", textAlign: "left" }}>
                              Rep
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Hours
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Rate
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Calls
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              km
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Deducted km
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Distance
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Expenses
                            </th>
                            <th style={{ padding: "8px", textAlign: "right" }}>
                              Adjustments
                            </th>
                            <th
                              style={{
                                padding: "8px",
                                textAlign: "right",
                                fontWeight: "bold",
                              }}
                            >
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {userSummary.map((user) => {
                            const isExpanded = expandedRows[user.userId];

                            // Calculate values from transactions for this user
                            const userIdStr = user.userId;

                            const userTransactions = transactions.filter(
                              (t) => {
                                return t.user.id === userIdStr;
                              },
                            );

                            const userCalls = userTransactions.filter(
                              (t) => t.transactionType === "call",
                            );
                            const userMileage = userTransactions.filter(
                              (t) =>
                                t.transactionType === "mileage" ||
                                t.transactionType === "homeMileage",
                            );
                            const userExpenses = userTransactions.filter(
                              (t) => t.transactionType === "expense",
                            );

                            const totalHours = userCalls.reduce(
                              (sum, c) =>
                                sum + (c.calculationDetails?.hoursWorked || 0),
                              0,
                            );
                            const callsAmount = userCalls.reduce(
                              (sum, c) => sum + c.amount,
                              0,
                            );
                            const mileageAmount = userMileage.reduce(
                              (sum, m) => sum + m.amount,
                              0,
                            );
                            const totalMileageKm = userMileage.reduce(
                              (sum, m) => {
                                const cd = m.calculationDetails || {};
                                const submitted =
                                  Number(cd.callMileage || 0) +
                                    Number(cd.homeMileage || 0) ||
                                  Number(cd.mileage || 0);
                                return sum + submitted;
                              },
                              0,
                            );
                            const totalDeductedKm = userMileage.reduce(
                              (sum, m) => {
                                const cd = m.calculationDetails || {};
                                const submitted =
                                  Number(cd.callMileage || 0) +
                                    Number(cd.homeMileage || 0) ||
                                  Number(cd.mileage || 0);
                                const reimbursed =
                                  Number(cd.reimbursedCallMileage || 0) +
                                    Number(cd.reimbursedHomeMileage || 0) ||
                                  Number(cd.reimbursedMileage ?? submitted);
                                const deducted = Math.max(
                                  submitted - reimbursed,
                                  0,
                                );
                                return sum + deducted;
                              },
                              0,
                            );
                            const expensesAmount = userExpenses.reduce(
                              (sum, e) => sum + e.amount,
                              0,
                            );
                            const userAdjustments = adjustments.filter(
                              (a) => a.user?.id === userIdStr,
                            );
                            const adjustmentsAmount = userAdjustments.reduce(
                              (sum, a) => sum + (a.amount || 0),
                              0,
                            );
                            const blendedRate =
                              totalHours > 0
                                ? (callsAmount / totalHours).toFixed(2)
                                : 0;
                            const rateDisplay =
                              blendedRate > 0 ? `$${blendedRate}` : "-";

                            return (
                              <>
                                <tr
                                  style={{
                                    borderBottom: "1px solid #eee",
                                    cursor: "pointer",
                                    backgroundColor: isExpanded
                                      ? "#f5f5f5"
                                      : "transparent",
                                  }}
                                >
                                  <td style={{ padding: "8px" }}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setExpandedRows((prev) => ({
                                          ...prev,
                                          [user.userId]: !isExpanded,
                                        }))
                                      }
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </IconButton>
                                  </td>
                                  <td style={{ padding: "8px" }}>
                                    <Typography variant="body2">
                                      {user.userName ||
                                        user.email ||
                                        user.userId}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {totalHours.toFixed(3)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {rateDisplay}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      ${callsAmount.toFixed(2)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {Math.round(totalMileageKm)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {Math.round(totalDeductedKm)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      ${mileageAmount.toFixed(2)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      ${expensesAmount.toFixed(2)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      ${adjustmentsAmount.toFixed(2)}
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      ${user.totalAmount?.toFixed(2) || "0.00"}
                                    </Typography>
                                  </td>
                                </tr>

                                {/* Expanded Details Row */}
                                {isExpanded && (
                                  <tr
                                    style={{
                                      backgroundColor: "#fafafa",
                                      borderBottom: "2px solid #ddd",
                                    }}
                                  >
                                    <td
                                      colSpan="11"
                                      style={{ padding: "16px" }}
                                    >
                                      <Box>
                                        {/* Calls from transactions */}
                                        {(() => {
                                          const userIdStr = user.userId;

                                          const userTransactions =
                                            transactions.filter(
                                              (t) => t.user.id === userIdStr,
                                            );

                                          const callTransactions =
                                            userTransactions.filter(
                                              (t) =>
                                                t.transactionType === "call",
                                            );

                                          const mileageMap = {};
                                          const homeMileageMap = {};
                                          userTransactions
                                            .filter(
                                              (t) =>
                                                t.transactionType === "mileage",
                                            )
                                            .forEach((m) => {
                                              const key =
                                                m.call?.id ||
                                                m.call?._id ||
                                                m.callId?.id ||
                                                m.callId?._id ||
                                                m.callId;
                                              if (!key) return;
                                              mileageMap[key] = m;
                                            });

                                          userTransactions
                                            .filter(
                                              (t) =>
                                                t.transactionType ===
                                                "homeMileage",
                                            )
                                            .forEach((m) => {
                                              const key =
                                                m.call?.id ||
                                                m.call?._id ||
                                                m.callId?.id ||
                                                m.callId?._id ||
                                                m.callId;
                                              if (!key) return;
                                              homeMileageMap[key] = m;
                                            });

                                          return callTransactions.length > 0 ? (
                                            <Box sx={{ mb: 2 }}>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                Calls:
                                              </Typography>
                                              <Box
                                                sx={{
                                                  ml: 2,
                                                  mt: 1,
                                                  overflow: "auto",
                                                }}
                                              >
                                                <table
                                                  style={{
                                                    width: "100%",
                                                    borderCollapse: "collapse",
                                                  }}
                                                >
                                                  <thead>
                                                    <tr
                                                      style={{
                                                        borderBottom:
                                                          "1px solid #ddd",
                                                      }}
                                                    >
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Date
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Project
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Location
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        City
                                                      </th>

                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        Hrs
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        Rate/hr
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        Pay $
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        Mileage $
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        KM
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        First/Last
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        Action
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {callTransactions.map(
                                                      (call) => {
                                                        const enriched =
                                                          enrichTransactionWithCallDetails(
                                                            call,
                                                          );
                                                        const callRef =
                                                          call.call?.id ||
                                                          call.call?._id ||
                                                          call.callId?.id ||
                                                          call.callId?._id ||
                                                          call.callId;
                                                        const mileage =
                                                          mileageMap[callRef] ||
                                                          null;
                                                        const homeMileage =
                                                          homeMileageMap[
                                                            callRef
                                                          ] || null;

                                                        const mileageCalc =
                                                          mileage?.calculationDetails ||
                                                          {};
                                                        const homeCalc =
                                                          homeMileage?.calculationDetails ||
                                                          {};

                                                        const legacyHomeRaw =
                                                          Number(
                                                            mileageCalc.homeMileage ||
                                                              0,
                                                          );
                                                        const legacyHomeReimb =
                                                          Number(
                                                            mileageCalc.reimbursedHomeMileage ||
                                                              Math.max(
                                                                legacyHomeRaw -
                                                                  Number(
                                                                    mileageCalc.lastCallDeductionKm ||
                                                                      0,
                                                                  ),
                                                                0,
                                                              ),
                                                          );

                                                        const toCallSubmitted =
                                                          Number(
                                                            mileageCalc.callMileage ||
                                                              Math.max(
                                                                Number(
                                                                  mileageCalc.mileage ||
                                                                    0,
                                                                ) -
                                                                  legacyHomeRaw,
                                                                0,
                                                              ),
                                                          );
                                                        const toCallReimbursed =
                                                          Number(
                                                            mileageCalc.reimbursedCallMileage ||
                                                              Math.max(
                                                                toCallSubmitted -
                                                                  Number(
                                                                    mileageCalc.firstCallDeductionKm ||
                                                                      0,
                                                                  ),
                                                                0,
                                                              ),
                                                          );

                                                        const homeSubmitted =
                                                          Number(
                                                            homeCalc.homeMileage ||
                                                              homeCalc.mileage ||
                                                              legacyHomeRaw ||
                                                              0,
                                                          );
                                                        const homeReimbursed =
                                                          Number(
                                                            homeCalc.reimbursedHomeMileage ||
                                                              homeCalc.reimbursedMileage ||
                                                              legacyHomeReimb ||
                                                              Math.max(
                                                                homeSubmitted -
                                                                  Number(
                                                                    homeCalc.lastCallDeductionKm ||
                                                                      0,
                                                                  ),
                                                                0,
                                                              ),
                                                          );

                                                        const submittedKm =
                                                          toCallSubmitted +
                                                          homeSubmitted;
                                                        const reimbursedKm =
                                                          toCallReimbursed +
                                                          homeReimbursed;
                                                        const callCalc =
                                                          call.calculationDetails ||
                                                          {};
                                                        const isFirst =
                                                          !!callCalc.firstCall;
                                                        const isLast =
                                                          !!callCalc.lastCall;
                                                        const firstLastLabel =
                                                          isFirst && isLast
                                                            ? "First/Last"
                                                            : isFirst
                                                              ? "First"
                                                              : isLast
                                                                ? "Last"
                                                                : "";
                                                        const totalAmount =
                                                          (call.amount || 0) +
                                                          (mileage?.amount ||
                                                            0) +
                                                          (homeMileage?.amount ||
                                                            0);

                                                        return (
                                                          <tr
                                                            key={call._id}
                                                            style={{
                                                              borderBottom:
                                                                "1px solid #eee",
                                                            }}
                                                          >
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                whiteSpace:
                                                                  "nowrap",
                                                              }}
                                                            >
                                                              {format(
                                                                new Date(
                                                                  enriched.scheduleDate,
                                                                ),
                                                                "MMM dd",
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                              }}
                                                            >
                                                              {
                                                                enriched.projectName
                                                              }
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                fontSize:
                                                                  "0.85rem",
                                                              }}
                                                            >
                                                              <Box>
                                                                <Typography
                                                                  sx={{
                                                                    fontSize:
                                                                      "12px",
                                                                    fontWeight: 500,
                                                                    lineHeight: 1.2,
                                                                  }}
                                                                >
                                                                  {
                                                                    enriched.locationName
                                                                  }
                                                                  {enriched.locationNumber
                                                                    ? ` (${enriched.locationNumber})`
                                                                    : ""}
                                                                </Typography>
                                                                {(enriched.locationCity ||
                                                                  enriched.locationProvince) && (
                                                                  <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                      fontSize:
                                                                        "10px",
                                                                      display:
                                                                        "block",
                                                                      lineHeight: 1.2,
                                                                    }}
                                                                  >
                                                                    {[
                                                                      enriched.locationCity,
                                                                      enriched.locationProvince,
                                                                    ]
                                                                      .filter(
                                                                        Boolean,
                                                                      )
                                                                      .join(
                                                                        ", ",
                                                                      )}
                                                                  </Typography>
                                                                )}
                                                              </Box>
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                fontSize:
                                                                  "0.85rem",
                                                              }}
                                                            >
                                                              {enriched.locationCity ||
                                                                ""}
                                                            </td>

                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "right",
                                                              }}
                                                            >
                                                              {enriched.hoursWorked?.toFixed(
                                                                3,
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "right",
                                                              }}
                                                            >
                                                              $
                                                              {
                                                                enriched.hourlyRate
                                                              }
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "right",
                                                              }}
                                                            >
                                                              {formatCurrency(
                                                                totalAmount,
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "right",
                                                              }}
                                                            >
                                                              {formatCurrency(
                                                                (mileage?.amount ||
                                                                  0) +
                                                                  (homeMileage?.amount ||
                                                                    0),
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "right",
                                                              }}
                                                            >
                                                              {submittedKm !=
                                                              null
                                                                ? `${submittedKm} -> ${
                                                                    reimbursedKm ??
                                                                    submittedKm
                                                                  }`
                                                                : "-"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "left",
                                                              }}
                                                            >
                                                              {firstLastLabel ||
                                                                "-"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "4px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              {(() => {
                                                                const callId =
                                                                  call.callId;
                                                                if (!callId) {
                                                                  return (
                                                                    <Typography
                                                                      variant="caption"
                                                                      color="text.secondary"
                                                                    >
                                                                      N/A
                                                                    </Typography>
                                                                  );
                                                                }
                                                                return (
                                                                  <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    justifyContent="center"
                                                                  >
                                                                    <Button
                                                                      size="small"
                                                                      onClick={() =>
                                                                        handleEditCall(
                                                                          callId,
                                                                        )
                                                                      }
                                                                    >
                                                                      Edit
                                                                    </Button>
                                                                    <Button
                                                                      size="small"
                                                                      color="error"
                                                                      onClick={() =>
                                                                        handleRemoveCall(
                                                                          callId,
                                                                        )
                                                                      }
                                                                    >
                                                                      Remove
                                                                    </Button>
                                                                  </Stack>
                                                                );
                                                              })()}
                                                            </td>
                                                          </tr>
                                                        );
                                                      },
                                                    )}
                                                  </tbody>
                                                </table>
                                              </Box>
                                            </Box>
                                          ) : null;
                                        })()}

                                        {/* Expenses from transactions */}
                                        {(() => {
                                          const userIdStr = user.userId;
                                          const userExpenses =
                                            transactions.filter(
                                              (t) =>
                                                t.user.id === userIdStr &&
                                                t.transactionType === "expense",
                                            );

                                          return userExpenses.length > 0 ? (
                                            <Box>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                Expenses:
                                              </Typography>
                                              <Box
                                                sx={{
                                                  ml: 2,
                                                  mt: 1,
                                                  overflow: "auto",
                                                }}
                                              >
                                                <table
                                                  style={{
                                                    width: "100%",
                                                    borderCollapse: "collapse",
                                                  }}
                                                >
                                                  <thead>
                                                    <tr
                                                      style={{
                                                        borderBottom:
                                                          "1px solid #ddd",
                                                      }}
                                                    >
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Date
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Type
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Description
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        Amount
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        Receipt
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {userExpenses.map((exp) => (
                                                      <tr
                                                        key={exp._id}
                                                        style={{
                                                          borderBottom:
                                                            "1px solid #eee",
                                                        }}
                                                      >
                                                        <td
                                                          style={{
                                                            padding: "4px",
                                                            whiteSpace:
                                                              "nowrap",
                                                          }}
                                                        >
                                                          {format(
                                                            new Date(
                                                              exp.transactionDate,
                                                            ),
                                                            "MMM dd",
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "4px",
                                                          }}
                                                        >
                                                          {exp.expenseType ||
                                                            exp
                                                              .calculationDetails
                                                              ?.expenseType ||
                                                            "expense"}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "4px",
                                                            fontSize: "0.85rem",
                                                          }}
                                                        >
                                                          {exp
                                                            .calculationDetails
                                                            ?.description ||
                                                            "-"}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "4px",
                                                            textAlign: "right",
                                                          }}
                                                        >
                                                          {formatCurrency(
                                                            exp.amount,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "4px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {exp.hasReceipts ? (
                                                            <a
                                                              href="#"
                                                              onClick={(e) => {
                                                                e.preventDefault();
                                                                handleViewReceipts(
                                                                  exp.expenseId,
                                                                  exp
                                                                    .calculationDetails
                                                                    ?.description,
                                                                );
                                                              }}
                                                              style={{
                                                                color:
                                                                  "#1976d2",
                                                                textDecoration:
                                                                  "none",
                                                                fontSize:
                                                                  "0.75rem",
                                                                cursor:
                                                                  "pointer",
                                                              }}
                                                            >
                                                              View
                                                            </a>
                                                          ) : (
                                                            <Typography
                                                              variant="caption"
                                                              color="text.secondary"
                                                            >
                                                              -
                                                            </Typography>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </Box>
                                            </Box>
                                          ) : null;
                                        })()}

                                        {/* Adjustments */}
                                        {(() => {
                                          const userIdStr = user.userId;
                                          const userAdjustments =
                                            adjustments.filter(
                                              (a) => a.user?.id === userIdStr,
                                            );

                                          return userAdjustments.length > 0 ? (
                                            <Box sx={{ mt: 2 }}>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                Adjustments:
                                              </Typography>
                                              <Box
                                                sx={{
                                                  ml: 2,
                                                  mt: 1,
                                                  overflow: "auto",
                                                }}
                                              >
                                                <table
                                                  style={{
                                                    width: "100%",
                                                    borderCollapse: "collapse",
                                                  }}
                                                >
                                                  <thead>
                                                    <tr
                                                      style={{
                                                        borderBottom:
                                                          "1px solid #ddd",
                                                      }}
                                                    >
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Type
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "right",
                                                        }}
                                                      >
                                                        Amount
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Status
                                                      </th>
                                                      <th
                                                        style={{
                                                          padding: "4px",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        Notes
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {userAdjustments.map(
                                                      (adj) => (
                                                        <tr
                                                          key={adj.id}
                                                          style={{
                                                            borderBottom:
                                                              "1px solid #eee",
                                                          }}
                                                        >
                                                          <td
                                                            style={{
                                                              padding: "4px",
                                                              textTransform:
                                                                "capitalize",
                                                            }}
                                                          >
                                                            {adj.type}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding: "4px",
                                                              textAlign:
                                                                "right",
                                                            }}
                                                          >
                                                            {formatCurrency(
                                                              adj.amount,
                                                            )}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding: "4px",
                                                              textTransform:
                                                                "capitalize",
                                                              color:
                                                                adj.status ===
                                                                "approved"
                                                                  ? "green"
                                                                  : "orange",
                                                            }}
                                                          >
                                                            {adj.status}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding: "4px",
                                                              fontSize:
                                                                "0.85rem",
                                                            }}
                                                          >
                                                            {adj.notes || "-"}
                                                          </td>
                                                        </tr>
                                                      ),
                                                    )}
                                                  </tbody>
                                                </table>
                                              </Box>
                                            </Box>
                                          ) : null;
                                        })()}
                                      </Box>
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                )}

                {/* Transactions Tab */}
                {activeTab === 1 && (
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                    >
                      Transactions ({transactions.length})
                    </Typography>
                    {transactions.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        No transactions yet
                      </Typography>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        {(() => {
                          // Group transactions by project
                          const groupedByProject = {};
                          transactions.forEach((t) => {
                            const projectName = t.project?.name || "No Project";
                            if (!groupedByProject[projectName]) {
                              groupedByProject[projectName] = [];
                            }
                            groupedByProject[projectName].push(t);
                          });

                          const projectNames =
                            Object.keys(groupedByProject).sort();

                          return (
                            <Box>
                              {projectNames.map((projectName) => {
                                const projectTransactionsRaw =
                                  groupedByProject[projectName];

                                const toTimestamp = (value) => {
                                  if (!value) return Number.MAX_SAFE_INTEGER;
                                  const dt = new Date(value);
                                  const millis = dt.getTime();
                                  return Number.isFinite(millis)
                                    ? millis
                                    : Number.MAX_SAFE_INTEGER;
                                };

                                const getCallGroupKey = (tx) => {
                                  const callRef = tx?.call;
                                  if (typeof callRef === "string")
                                    return callRef;
                                  if (callRef?.id) return callRef.id;
                                  if (callRef?._id) return callRef._id;

                                  if (typeof tx?.callId === "string")
                                    return tx.callId;
                                  if (tx?.callId?.id) return tx.callId.id;
                                  if (tx?.callId?._id) return tx.callId._id;

                                  return null;
                                };

                                const callGroups = new Map();
                                const standaloneTransactions = [];

                                (projectTransactionsRaw || []).forEach((tx) => {
                                  const isCallOrMileage =
                                    tx?.transactionType === "call" ||
                                    tx?.transactionType === "mileage" ||
                                    tx?.transactionType === "homeMileage";
                                  const callKey = getCallGroupKey(tx);

                                  if (isCallOrMileage && callKey) {
                                    if (!callGroups.has(callKey)) {
                                      callGroups.set(callKey, {
                                        callTx: null,
                                        mileageTx: null,
                                        homeMileageTx: null,
                                        overflow: [],
                                      });
                                    }

                                    const group = callGroups.get(callKey);
                                    if (tx.transactionType === "call") {
                                      if (!group.callTx) {
                                        group.callTx = tx;
                                      } else {
                                        group.overflow.push(tx);
                                      }
                                    } else if (
                                      tx.transactionType === "mileage"
                                    ) {
                                      if (!group.mileageTx) {
                                        group.mileageTx = tx;
                                      } else {
                                        group.overflow.push(tx);
                                      }
                                    } else if (
                                      tx.transactionType === "homeMileage"
                                    ) {
                                      if (!group.homeMileageTx) {
                                        group.homeMileageTx = tx;
                                      } else {
                                        group.overflow.push(tx);
                                      }
                                    }
                                  } else {
                                    standaloneTransactions.push(tx);
                                  }
                                });

                                const orderedFromCallGroups = Array.from(
                                  callGroups.values(),
                                )
                                  .sort((a, b) => {
                                    const aStartedAt = toTimestamp(
                                      a.callTx?.call?.startedAt ||
                                        a.callTx?.transactionDate ||
                                        a.mileageTx?.call?.startedAt ||
                                        a.mileageTx?.transactionDate ||
                                        a.homeMileageTx?.call?.startedAt ||
                                        a.homeMileageTx?.transactionDate,
                                    );
                                    const bStartedAt = toTimestamp(
                                      b.callTx?.call?.startedAt ||
                                        b.callTx?.transactionDate ||
                                        b.mileageTx?.call?.startedAt ||
                                        b.mileageTx?.transactionDate ||
                                        b.homeMileageTx?.call?.startedAt ||
                                        b.homeMileageTx?.transactionDate,
                                    );

                                    if (aStartedAt !== bStartedAt) {
                                      return aStartedAt - bStartedAt;
                                    }

                                    const aFallback = String(
                                      a.callTx?.id ||
                                        a.callTx?._id ||
                                        a.mileageTx?.id ||
                                        a.mileageTx?._id ||
                                        a.homeMileageTx?.id ||
                                        a.homeMileageTx?._id ||
                                        "",
                                    );
                                    const bFallback = String(
                                      b.callTx?.id ||
                                        b.callTx?._id ||
                                        b.mileageTx?.id ||
                                        b.mileageTx?._id ||
                                        b.homeMileageTx?.id ||
                                        b.homeMileageTx?._id ||
                                        "",
                                    );
                                    return aFallback.localeCompare(bFallback);
                                  })
                                  .flatMap((group) => {
                                    const rows = [];
                                    const basis =
                                      group.callTx ||
                                      group.mileageTx ||
                                      group.homeMileageTx;

                                    if (basis) {
                                      const callCd =
                                        group.callTx?.calculationDetails || {};
                                      const mileageCd =
                                        group.mileageTx?.calculationDetails ||
                                        {};
                                      const homeCd =
                                        group.homeMileageTx
                                          ?.calculationDetails || {};

                                      const legacyHomeRaw = Number(
                                        mileageCd.homeMileage || 0,
                                      );
                                      const toCallSubmitted = Number(
                                        mileageCd.callMileage ||
                                          Math.max(
                                            Number(mileageCd.mileage || 0) -
                                              legacyHomeRaw,
                                            0,
                                          ),
                                      );
                                      const toCallReimbursed = Number(
                                        mileageCd.reimbursedCallMileage ||
                                          Math.max(
                                            toCallSubmitted -
                                              Number(
                                                mileageCd.firstCallDeductionKm ||
                                                  0,
                                              ),
                                            0,
                                          ),
                                      );

                                      const homeSubmitted = Number(
                                        homeCd.homeMileage ||
                                          homeCd.mileage ||
                                          legacyHomeRaw ||
                                          0,
                                      );
                                      const homeReimbursed = Number(
                                        homeCd.reimbursedHomeMileage ||
                                          homeCd.reimbursedMileage ||
                                          Math.max(
                                            homeSubmitted -
                                              Number(
                                                homeCd.lastCallDeductionKm ||
                                                  mileageCd.lastCallDeductionKm ||
                                                  0,
                                              ),
                                            0,
                                          ),
                                      );

                                      rows.push({
                                        ...basis,
                                        transactionType: "call",
                                        amount:
                                          Number(group.callTx?.amount || 0) +
                                          Number(group.mileageTx?.amount || 0) +
                                          Number(
                                            group.homeMileageTx?.amount || 0,
                                          ),
                                        status:
                                          group.callTx?.status ||
                                          group.mileageTx?.status ||
                                          group.homeMileageTx?.status ||
                                          "pending",
                                        errorReason:
                                          group.callTx?.errorReason ||
                                          group.mileageTx?.errorReason ||
                                          group.homeMileageTx?.errorReason ||
                                          null,
                                        call:
                                          group.callTx?.call ||
                                          group.mileageTx?.call ||
                                          group.homeMileageTx?.call ||
                                          basis.call,
                                        calculationDetails: {
                                          ...callCd,
                                          mileage:
                                            toCallSubmitted + homeSubmitted,
                                          callMileage: toCallSubmitted,
                                          homeMileage: homeSubmitted,
                                          reimbursedMileage:
                                            toCallReimbursed + homeReimbursed,
                                          reimbursedCallMileage:
                                            toCallReimbursed,
                                          reimbursedHomeMileage: homeReimbursed,
                                          firstCallDeductionKm: Number(
                                            mileageCd.firstCallDeductionKm || 0,
                                          ),
                                          lastCallDeductionKm: Number(
                                            homeCd.lastCallDeductionKm ||
                                              mileageCd.lastCallDeductionKm ||
                                              0,
                                          ),
                                        },
                                      });
                                    }

                                    if (Array.isArray(group.overflow)) {
                                      rows.push(
                                        ...group.overflow.sort(
                                          (x, y) =>
                                            toTimestamp(x.transactionDate) -
                                            toTimestamp(y.transactionDate),
                                        ),
                                      );
                                    }

                                    return rows;
                                  });

                                const projectTransactions = [
                                  ...orderedFromCallGroups,
                                  ...standaloneTransactions.sort(
                                    (a, b) =>
                                      toTimestamp(a.transactionDate) -
                                      toTimestamp(b.transactionDate),
                                  ),
                                ];
                                const isExpanded =
                                  expandedRows[`project-${projectName}`];

                                const projectTotal = projectTransactions.reduce(
                                  (sum, t) => sum + t.amount,
                                  0,
                                );

                                return (
                                  <Box
                                    key={projectName}
                                    sx={{ mb: 2 }}
                                  >
                                    {/* Project Header */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "12px",
                                        backgroundColor: "#f5f5f5",
                                        border: "1px solid #ddd",
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: "#ebebeb",
                                        },
                                      }}
                                      onClick={() =>
                                        setExpandedRows((prev) => ({
                                          ...prev,
                                          [`project-${projectName}`]:
                                            !isExpanded,
                                        }))
                                      }
                                    >
                                      <span style={{ marginRight: "8px" }}>
                                        {isExpanded ? "▼" : "▶"}
                                      </span>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: "bold", flex: 1 }}
                                      >
                                        {projectName}
                                      </Typography>
                                      <Typography variant="body2">
                                        {projectTransactions.length}{" "}
                                        transactions • $
                                        {projectTotal.toFixed(2)}
                                      </Typography>
                                    </Box>

                                    {/* Expanded Transactions Table */}
                                    {isExpanded && (
                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        <thead>
                                          <tr
                                            style={{
                                              borderBottom: "1px solid #ddd",
                                              backgroundColor: "#fafafa",
                                            }}
                                          >
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "left",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Type
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "left",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Date
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "left",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Rep
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "left",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Location
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Hours
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Rate
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Mileage
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Deductions
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "left",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              First/Last
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Amount
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "left",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Status
                                            </th>
                                            <th
                                              style={{
                                                padding: "8px",
                                                textAlign: "center",
                                                fontSize: "0.85rem",
                                              }}
                                            >
                                              Action
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {projectTransactions.map((t) => {
                                            const key = t._id || t.id;
                                            const userDisplay =
                                              t.user?.legalName ||
                                              t.user?.name ||
                                              t.user?.email ||
                                              t.userId ||
                                              "";
                                            const cd =
                                              t.calculationDetails || {};
                                            const hours =
                                              cd.hoursWorked ?? null;
                                            const rate = cd.hourlyRate ?? null;
                                            const mileage = cd.mileage ?? null;
                                            const firstDed =
                                              cd.firstCallDeductionKm ?? 0;
                                            const lastDed =
                                              cd.lastCallDeductionKm ?? 0;
                                            const reimbursed =
                                              cd.reimbursedMileage ?? null;
                                            const amount = t.amount;
                                            const isFirst =
                                              !!cd.firstCall || false;
                                            const isLast =
                                              !!cd.lastCall || false;
                                            const firstLastLabel =
                                              isFirst && isLast
                                                ? "First/Last"
                                                : isFirst
                                                  ? "First"
                                                  : isLast
                                                    ? "Last"
                                                    : "";
                                            return (
                                              <tr
                                                key={key}
                                                style={{
                                                  borderBottom:
                                                    "1px solid #eee",
                                                  fontSize: "0.85rem",
                                                }}
                                              >
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {t.transactionType}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {t.transactionDate
                                                      ? format(
                                                          new Date(
                                                            t.transactionDate,
                                                          ),
                                                          "MMM dd",
                                                        )
                                                      : "-"}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {userDisplay}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    noWrap
                                                  >
                                                    {t.location?.name || ""}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {hours != null
                                                      ? hours.toFixed(3)
                                                      : "-"}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {rate != null
                                                      ? `$${rate}`
                                                      : "-"}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {mileage != null
                                                      ? mileage
                                                      : "-"}
                                                    {reimbursed != null && (
                                                      <>
                                                        {" "}
                                                        <span
                                                          style={{
                                                            color: "#888",
                                                          }}
                                                        >
                                                          (reimbursed{" "}
                                                          {reimbursed})
                                                        </span>
                                                      </>
                                                    )}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {firstDed || lastDed
                                                      ? `${firstDed || 0}/${
                                                          lastDed || 0
                                                        } km`
                                                      : "-"}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {firstLastLabel || "-"}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {formatCurrency(amount)}
                                                  </Typography>
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color={
                                                      t.status === "error"
                                                        ? "error"
                                                        : "textPrimary"
                                                    }
                                                  >
                                                    {t.status}
                                                  </Typography>
                                                  {t.errorReason && (
                                                    <Typography
                                                      variant="caption"
                                                      color="error"
                                                    >
                                                      {t.errorReason}
                                                    </Typography>
                                                  )}
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "6px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {(t.transactionType ===
                                                    "call" ||
                                                    t.transactionType ===
                                                      "mileage") &&
                                                    t.call?.id && (
                                                      <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        justifyContent="center"
                                                      >
                                                        <Button
                                                          size="small"
                                                          onClick={() =>
                                                            handleEditCall(
                                                              t.call.id,
                                                            )
                                                          }
                                                        >
                                                          Edit
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          color="error"
                                                          onClick={() =>
                                                            handleRemoveCall(
                                                              t.call.id,
                                                            )
                                                          }
                                                        >
                                                          Remove
                                                        </Button>
                                                      </Stack>
                                                    )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Pending Expenses Tab */}
                {activeTab === 2 && (
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                    >
                      Pending Expense Approvals ({pendingExpenses.length})
                    </Typography>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr style={{ borderBottom: "2px solid #ddd" }}>
                          <th style={{ padding: "8px", textAlign: "left" }}>
                            Date
                          </th>
                          <th style={{ padding: "8px", textAlign: "left" }}>
                            Rep
                          </th>
                          <th style={{ padding: "8px", textAlign: "left" }}>
                            Type
                          </th>
                          <th style={{ padding: "8px", textAlign: "left" }}>
                            Description
                          </th>
                          <th style={{ padding: "8px", textAlign: "right" }}>
                            Amount
                          </th>
                          <th style={{ padding: "8px", textAlign: "center" }}>
                            Receipt
                          </th>
                          <th style={{ padding: "8px", textAlign: "center" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingExpenses.map((expense) => (
                          <tr
                            key={expense._id}
                            style={{ borderBottom: "1px solid #eee" }}
                          >
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2">
                                {formatDate(expense.expenseDate)}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2">
                                {expense.user?.legalName ||
                                  expense.user?.name ||
                                  "Unknown"}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2">
                                {getExpenseTypeLabel(expense)}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Typography
                                variant="body2"
                                noWrap
                              >
                                {expense.description}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <Typography variant="body2">
                                {getExpenseValueLabel(expense)}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px", textAlign: "center" }}>
                              {expense.receipts &&
                              expense.receipts.length > 0 ? (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewReceipts(
                                      expense.id || expense._id,
                                      expense.description,
                                    );
                                  }}
                                  style={{
                                    color: "#1976d2",
                                    textDecoration: "none",
                                    fontSize: "0.875rem",
                                    cursor: "pointer",
                                  }}
                                >
                                  View
                                </a>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "center" }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="center"
                              >
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() =>
                                    openApprovalDialog(expense, "approve")
                                  }
                                  disabled={expensesLoading}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    openApprovalDialog(expense, "reject")
                                  }
                                  disabled={expensesLoading}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}

                {/* Adjustments Tab */}
                {activeTab === 3 && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">
                        Adjustments ({adjustments?.length || 0})
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setSelectedAdjustment(null);
                          setShowAdjustmentModal(true);
                        }}
                      >
                        Add Adjustment
                      </Button>
                    </Box>

                    {adjustments && adjustments.length > 0 ? (
                      <Box sx={{ overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "12px",
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: "#f5f5f5" }}>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                }}
                              >
                                User
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Project
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Type
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "right",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Amount
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Status
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Created By
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Notes
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  border: "1px solid #ddd",
                                }}
                              >
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {adjustments.map((adjustment) => (
                              <tr key={adjustment.id}>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {adjustment.user?.legalName ||
                                    adjustment.user?.name ||
                                    "Unknown"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {adjustment.project?.name || "Unknown"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {adjustment.type}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "right",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  ${adjustment.amount?.toFixed(2) || "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                    fontWeight:
                                      adjustment.status === "approved"
                                        ? "bold"
                                        : "normal",
                                    color:
                                      adjustment.status === "approved"
                                        ? "green"
                                        : "orange",
                                  }}
                                >
                                  {adjustment.status}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {adjustment.createdByUser?.name || "Unknown"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                    maxWidth: "200px",
                                  }}
                                >
                                  {adjustment.notes}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                    textAlign: "center",
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="center"
                                  >
                                    {!adjustment.isLocked && (
                                      <>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setSelectedAdjustment(adjustment);
                                            setShowAdjustmentModal(true);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          size="small"
                                          color="error"
                                          onClick={() => {
                                            setDeleteConfirm({
                                              open: true,
                                              adjustmentId: adjustment.id,
                                            });
                                          }}
                                        >
                                          Delete
                                        </Button>
                                      </>
                                    )}
                                    {adjustment.status === "pending" &&
                                      !adjustment.isLocked && (
                                        <Button
                                          size="small"
                                          color="success"
                                          onClick={() =>
                                            handleApproveAdjustmentClick(
                                              adjustment.id,
                                            )
                                          }
                                        >
                                          Approve
                                        </Button>
                                      )}
                                  </Stack>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        No adjustments for this pay period
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Exports Tab */}
                {activeTab === 4 && (
                  <Box sx={{ mt: 2 }}>
                    <PayrollExports
                      payPeriodId={payPeriodId}
                      payPeriod={payPeriod}
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Card>
      </Page>

      <ExpenseApprovalModal
        open={approvalDialog.open}
        expense={approvalDialog.expense}
        initialAction={approvalDialog.initialAction}
        projects={projects}
        expenseAccounts={expenseAccounts}
        submitting={approvalSubmitting}
        onClose={closeApprovalDialog}
        onApprove={handleApproveExpense}
        onReject={handleRejectExpense}
      />

      {/* Phase 4: Changed Calls Dialog */}
      <Dialog
        open={showChangedCallsDialog}
        onClose={() => setShowChangedCallsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Changes Detected:{" "}
          {(changedCallsInfo?.changedCalls?.length || 0) +
            (changedCallsInfo?.newExpenses?.length || 0)}{" "}
          items
        </DialogTitle>
        <DialogContent>
          {/* Changed Calls Section */}
          {changedCallsInfo?.changedCalls &&
            changedCallsInfo.changedCalls.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Modified Calls ({changedCallsInfo.changedCalls.length})
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2 }}
                >
                  The following calls have been modified since the last
                  processing:
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>User</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Last Modified</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changedCallsInfo.changedCalls.map((call) => (
                        <TableRow key={call.callId}>
                          <TableCell>{call.userName}</TableCell>
                          <TableCell>{call.projectName}</TableCell>
                          <TableCell>{call.locationName}</TableCell>
                          <TableCell align="right">
                            {call.hoursWorked?.toFixed(2) || 0}
                          </TableCell>
                          <TableCell>{formatDate(call.updatedAt)}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedCallForEdit(call.callId);
                                setShowChangedCallsDialog(false);
                              }}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

          {/* Newly Approved Expenses Section */}
          {changedCallsInfo?.newExpenses &&
            changedCallsInfo.newExpenses.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Newly Approved Expenses ({changedCallsInfo.newExpenses.length}
                  )
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2 }}
                >
                  The following expenses were approved after the last
                  processing:
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>User</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Expense Date</TableCell>
                        <TableCell align="right">Approved On</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changedCallsInfo.newExpenses.map((expense) => (
                        <TableRow key={expense.expenseId}>
                          <TableCell>{expense.userName}</TableCell>
                          <TableCell>
                            {expense.expenseAccountName ||
                              expense.expenseCategory ||
                              expense.expenseType}
                          </TableCell>
                          <TableCell>{expense.projectName}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(expense.amount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDate(expense.expenseDate)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDate(expense.approvedOn)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

          {!changedCallsInfo?.changedCalls?.length &&
            !changedCallsInfo?.newExpenses?.length && (
              <Typography
                variant="body2"
                color="textSecondary"
              >
                No changes detected.
              </Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChangedCallsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 4: Reprocess Dialog */}
      <Dialog
        open={showReprocessDialog}
        onClose={() => setShowReprocessDialog(false)}
      >
        <DialogTitle>Reprocess Pay Period</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ mb: 3 }}
          >
            Choose how to reprocess the pay period:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={fullReprocess}
                onChange={(e) => setFullReprocess(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold" }}
                >
                  Full Reprocess
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  Recalculate all transactions for the entire pay period
                </Typography>
              </Box>
            }
          />
          {!fullReprocess && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ mt: 2 }}
            >
              Smart Reprocess (default): Only recalculate changed calls
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReprocessDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmReprocess}
            variant="contained"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ mr: 1 }}
                />
                Reprocessing...
              </>
            ) : (
              `Reprocess (${fullReprocess ? "Full" : "Smart"})`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 4: Close Pay Period Dialog */}
      <Dialog_YesNo
        open={showCloseDialog}
        onClose={() => setShowCloseDialog(false)}
        title="Close Pay Period"
        message={
          <>
            <Typography
              variant="body1"
              sx={{ mb: 2 }}
            >
              <strong>Warning: This action is irreversible.</strong>
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 2 }}
            >
              Closing this pay period will:
            </Typography>
            <ul>
              <li>Lock all calls and expenses</li>
              <li>Prevent any further changes to this period</li>
              <li>Future adjustments must be made in the next pay period</li>
            </ul>
            <Typography
              variant="body2"
              color="error"
            >
              This action cannot be undone.
            </Typography>
          </>
        }
        onYes={handleConfirmClose}
        onNo={() => setShowCloseDialog(false)}
        yesLabel="Close Pay Period"
        noLabel="Cancel"
      />

      {/* CallDocket Modal for Editing */}
      <CallDocketModal
        call={selectedCallForEdit}
        open={!!selectedCallForEdit}
        onRefresh={loadData}
        onClose={() => {
          setSelectedCallForEdit(null);
          loadData();
        }}
      />

      {/* Adjustment Modal */}
      <AdjustmentModal
        open={showAdjustmentModal}
        onClose={() => {
          setShowAdjustmentModal(false);
          setSelectedAdjustment(null);
        }}
        onSave={handleSaveAdjustment}
        adjustment={selectedAdjustment}
        users={adjustmentUsers}
        projects={adjustmentProjects}
        adjustmentTypes={adjustmentTypes}
        payPeriodId={payPeriodId}
        currentUserId={currentUserId}
      />

      {/* Message Dialog */}
      <Dialog
        open={messageDialog.open}
        onClose={() =>
          setMessageDialog({ open: false, title: "", message: "" })
        }
      >
        <DialogTitle>{messageDialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{messageDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setMessageDialog({ open: false, title: "", message: "" })
            }
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog
        open={receiptDialog.open}
        onClose={handleCloseReceiptDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Receipts - {receiptDialog.expenseDescription}</DialogTitle>
        <DialogContent>
          {receiptDialog.receipts.map((receipt, index) => (
            <Box
              key={index}
              sx={{ mb: 2 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Receipt {index + 1}
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <img
                  src={receipt}
                  alt={`Receipt ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseReceiptDialog}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Adjustment Confirmation */}
      <Dialog_YesNo
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, adjustmentId: null })}
        title="Delete Adjustment"
        message="Are you sure you want to delete this adjustment?"
        onYes={() => {
          handleDeleteAdjustment(deleteConfirm.adjustmentId);
          setDeleteConfirm({ open: false, adjustmentId: null });
        }}
        onNo={() => setDeleteConfirm({ open: false, adjustmentId: null })}
      />
    </>
  );
};

export default PayPeriodProcessing;
